import { get } from 'lodash';
import getColour from '@lendi-ui/commons/colours';
import ModeEdit from '@lendi-ui/icon/ModeEdit';
import MoreVert from '@lendi-ui/icon/MoreVert';
import MenuDropdown from '@lendi-ui/menu-dropdown';
import Tooltip from '@lendi-ui/tooltip';

import { FIELD_PATHS, FIELD_VALUES, PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { FormValuesALIEO, PropertyAsset } from '@funnels/types';
import { formatAreaAddressWithPostcode, run } from '@funnels/utils';

import { TempCard, TempCardContent, TempCardHeader, TempCardHeaderText } from '../../../Card/index.style';
import { PanelMenu, PanelMenuDropdownContentItem, PanelMenuIconButton } from '../_common/style';
import { Title } from './index.style';
import { PropertySummaryLayout } from './layouts';

interface PropertySummaryPanelProps {
  currentValues: FormValuesALIEO;
  handleModalShow: (id: string) => void;
  handleRedirect: (id: string) => void;
  handlePrimaryPurpose: (id: string, newPrimaryPurpose: FIELD_VALUES) => void;
  applicationLockIsActive?: boolean;
}

const ASSET_INDEX = 0;
const LABELS = {
  EDIT: 'Edit property',
  DELETE: 'Delete',
  REMOVE: 'Remove from loan',
  INCLUDE_AS_REFI: 'Move to refinance',
  INCLUDE_AS_NP: 'Move to new purchase',
  INCLUDE_AS_SECURITY: 'Move to security property',
};

const PropertySummaryPanel = (props: PropertySummaryPanelProps) => {
  const { tracking } = useAnalytics();
  const { currentValues, handleModalShow, handleRedirect, handlePrimaryPurpose, applicationLockIsActive } = props;
  const property: PropertyAsset = get(currentValues, FIELD_PATHS.ASSET(ASSET_INDEX));
  const toBeUsedAsSecurity = get(currentValues, FIELD_PATHS.ASSET_USED_AS_SECURITY(ASSET_INDEX));
  const country = get(currentValues, FIELD_PATHS.ASSET_ADDRESS_COUNTRY(ASSET_INDEX), 'Australia');
  const isAustralia = country === 'Australia';
  const title = formatAreaAddressWithPostcode(get(currentValues, FIELD_PATHS.ASSET_ADDRESS(ASSET_INDEX))) || 'Property';

  function handlePrimaryPurposeChange(value: FIELD_VALUES) {
    handlePrimaryPurpose(property.clientId, value);
  }

  function handleDeleteClick() {
    handleModalShow(property.clientId);
  }

  function handleTrackingElement(text: string) {
    tracking?.menu.elementClick({ menuName: 'Kebab menu', position: 'Property summary menu', text });
  }

  return (
    <TempCard>
      <TempCardHeader>
        <TempCardHeaderText>
          <Tooltip content={title} position="right">
            <Title className={PII_FIELD_MASKING_CLASS}>{title}</Title>
          </Tooltip>
        </TempCardHeaderText>
        <PanelMenu>
          <PanelMenuIconButton
            color={getColour('interaction', 'subtle')}
            size="sm"
            icon={ModeEdit}
            data-component="button-edit"
            onClick={() => {
              handleRedirect(property.clientId);
              tracking?.button.icon({ text: LABELS.EDIT, position: 'Property summary' });
            }}
          />
          {!applicationLockIsActive && (
            <MenuDropdown data-component="menu-dropdown">
              <MenuDropdown.Trigger data-component="menu-dropdown-trigger">
                <PanelMenuIconButton
                  color={getColour('interaction', 'subtle')}
                  size="sm"
                  icon={MoreVert}
                  onClick={() => tracking?.menu.click({ menuName: 'Kebab menu', position: 'Property summary' })}
                />
              </MenuDropdown.Trigger>
              <MenuDropdown.Content>
                {toBeUsedAsSecurity && (
                  <MenuDropdown.Content.Item
                    size="sm"
                    data-component="menu-dropdown-item"
                    onClick={run<void>(
                      () => handlePrimaryPurposeChange(FIELD_VALUES.OTHER),
                      () => handleTrackingElement(LABELS.REMOVE)
                    )}
                  >
                    <PanelMenuDropdownContentItem>{LABELS.REMOVE}</PanelMenuDropdownContentItem>
                  </MenuDropdown.Content.Item>
                )}
                {!toBeUsedAsSecurity && isAustralia && (
                  <>
                    <MenuDropdown.Content.Item
                      size="sm"
                      data-component="menu-dropdown-item"
                      onClick={run<void>(
                        () => handlePrimaryPurposeChange(FIELD_VALUES.REFINANCE),
                        () => handleTrackingElement(LABELS.INCLUDE_AS_REFI)
                      )}
                    >
                      <PanelMenuDropdownContentItem>{LABELS.INCLUDE_AS_REFI}</PanelMenuDropdownContentItem>
                    </MenuDropdown.Content.Item>
                    <MenuDropdown.Content.Item
                      size="sm"
                      data-component="menu-dropdown-item"
                      onClick={run<void>(
                        () => handlePrimaryPurposeChange(FIELD_VALUES.NEW_PURCHASE),
                        () => handleTrackingElement(LABELS.INCLUDE_AS_NP)
                      )}
                    >
                      <PanelMenuDropdownContentItem>{LABELS.INCLUDE_AS_NP}</PanelMenuDropdownContentItem>
                    </MenuDropdown.Content.Item>

                    <MenuDropdown.Content.Item
                      size="sm"
                      data-component="menu-dropdown-item"
                      onClick={run<void>(
                        () => handlePrimaryPurposeChange(FIELD_VALUES.SECURITY_PROPERTY),
                        () => handleTrackingElement(LABELS.INCLUDE_AS_SECURITY)
                      )}
                    >
                      <PanelMenuDropdownContentItem>{LABELS.INCLUDE_AS_SECURITY}</PanelMenuDropdownContentItem>
                    </MenuDropdown.Content.Item>
                  </>
                )}
                <MenuDropdown.Content.Item
                  size="sm"
                  data-component="menu-dropdown-item"
                  onClick={run<void>(handleDeleteClick, () => handleTrackingElement(LABELS.DELETE))}
                >
                  <PanelMenuDropdownContentItem>{LABELS.DELETE}</PanelMenuDropdownContentItem>
                </MenuDropdown.Content.Item>
              </MenuDropdown.Content>
            </MenuDropdown>
          )}
        </PanelMenu>
      </TempCardHeader>
      <TempCardContent>
        <PropertySummaryLayout currentValues={currentValues} />
      </TempCardContent>
    </TempCard>
  );
};

export default PropertySummaryPanel;
